import React, { Component } from 'react'
import PropTypes from 'prop-types'
import TranscriptWord from './TranscriptWord'
import escapeRegexString from '../utils/escapeRegexString'
import shallowEqual from 'fbjs/lib/shallowEqual'

export default class TranscriptView extends Component {
  static propTypes = {
    caseSensitive: PropTypes.bool,
    componentClassName: PropTypes.string,
    onMouseDown: PropTypes.func,
    onMouseUp: PropTypes.func,
    selectionClassName: PropTypes.string,
    selectionEndTime: PropTypes.number,
    selectionStartTime: PropTypes.number,
    text: PropTypes.string,
    timestamps: PropTypes.array,
    tokens: PropTypes.array
  }

  static defaultProps = {
    componentClassName: 'transcript-viewer',
    selectionClassName: 'qv-quote-selection',
    timestamps: []
  }

  shouldComponentUpdate(nextProps) {
    return !shallowEqual(this.props, nextProps)
  }

  matchOptions() {
    return this.props.caseSensitive ? 'g' : 'gi'
  }

  normalizeTokens() {
    const { tokens } = this.props

    if (!tokens) {
      return []
    }

    return (
      tokens &&
      tokens.reduce(
        (prev, value) => ({
          snippet: value.snippet,
          tokens: prev.tokens.concat(this.splitTokens(value.tokens)),
          fieldName: value.fieldName
        }),
        { tokens: [] }
      )
    )
  }

  splitTokens(tokens) {
    return tokens.reduce(
      (prev, token) => prev.concat(this.formattedToken(token)),
      []
    )
  }

  tokenMatches(token, word) {
    const regex = new RegExp(
      `(\\b${escapeRegexString(token || '')}\\b)`,
      this.matchOptions()
    )
    return regex.test(word)
  }

  highlightIndexes() {
    const { tokens, snippet, fieldName } = this.normalizeTokens()
    if (!tokens || !snippet) {
      return {}
    }
    const snippetWords = snippet.split(' ')
    const selectionOffset = this.selectionOffset(fieldName)

    return tokens.reduce((memo, { token, type }) => {
      for (let i = 0; i < snippetWords.length; i++) {
        // if type === 'inner', we don't want to match other cases that don't
        // border brackets, so we make sure that the previous instance is an
        // inner or outer token
        const shouldHighlight =
          snippetWords[i] === token &&
          (!['inner', 'end'].includes(type) ||
            ['inner', 'start'].includes(memo[i + selectionOffset - 1]))

        if (shouldHighlight) {
          memo[i + selectionOffset] = type || 'default'
        }
      }

      return memo
    }, {})
  }

  selectionOffset(type) {
    return type === 'text_selection' ? this.firstSelectedIndex() : 0
  }

  firstSelectedIndex() {
    const { selectionStartTime, timestamps } = this.props

    return timestamps.findIndex(timestamp => selectionStartTime <= timestamp)
  }

  formattedToken(token) {
    const tokens = token.split(' ')
    return tokens.map((_, i) => this.formatTokenByIndex(tokens, i))
  }

  formatTokenByIndex(tokens, index) {
    if (tokens.length === 1) {
      return { token: `[[${tokens[index]}]]` }
    }

    if (index === tokens.length - 1) {
      return { token: `${tokens[index]}]]`, type: 'end' }
    }

    if (index === 0) {
      return { token: `[[${tokens[index]}`, type: 'start' }
    }

    return { token: tokens[index], type: 'inner' }
  }

  isSelected(timestamp) {
    const { selectionStartTime, selectionEndTime } = this.props

    return selectionStartTime <= timestamp && timestamp <= selectionEndTime
  }

  render() {
    const {
      componentClassName,
      selectionClassName,
      text,
      timestamps,
      onMouseUp,
      onMouseDown
    } = this.props
    const words = text.split(' ')
    const highlightIndexes = this.highlightIndexes()

    return (
      <div
        className={componentClassName}
        onMouseUp={onMouseUp}
        onMouseDown={onMouseDown}
      >
        {words.map((word, index) => (
          <TranscriptWord
            word={word}
            key={index}
            timestamp={timestamps[index]}
            isSelected={this.isSelected(timestamps[index])}
            selectionClassName={selectionClassName}
            highlighted={!!highlightIndexes[index]}
            highlightSpace={['inner', 'end'].includes(highlightIndexes[index])}
          />
        ))}
      </div>
    )
  }
}



// WEBPACK FOOTER //
// ../src/components/TranscriptView.js